import {
    collection,
    getDocs,
    addDoc,
    query,
    where,
    doc
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { ValidationError, LessonError } from '@/shared/api/errors/ApiError';
import { getTimestamps, getUpdatedTimestamp, getCreatedTimestamp } from '@/shared/utils/api/timestampHelper';
import ServiceBase from '@/shared/api/base/ServiceBase';
import { QuizQuestion } from './quiz-services';
import { getLocalLessonContent } from './localContentLoader';

const LESSONS_COLLECTION = 'lessons';
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes for lessons

export interface Lesson {
    id: string;
    courseId: string;
    moduleId: string;
    title: string;
    description: string;
    type: string;
    order: number;
    videoUrl?: string | null;
    videoDuration?: number | null;
    textContent?: string | null;
    minimalMinutes?: number;
    includeInTimeTracking?: boolean;
    questions?: QuizQuestion[];
    passingScore?: number;
    content?: string;
    createdBy?: string;
    updatedBy?: string;
    createdAt?: string;
    updatedAt?: string;
    [key: string]: any;
}

class LessonService extends ServiceBase {
    constructor() {
        super('LessonService');
    }



    /**
     * Get all lessons for a module
     */
    async getLessons(courseId: string, moduleId: string): Promise<Lesson[]> {
        try {
            this.validate.validateCourseId(courseId);
            this.validate.validateModuleId(moduleId);

            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && (window as any).MOCK_LESSONS) {
                this.log('Returning MOCKED Lessons for E2E testing');
                return ((window as any).MOCK_LESSONS as Lesson[])
                    .filter(l => l.courseId === courseId && l.moduleId === moduleId)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
            }

            const cacheKey = `lessons_${courseId}_${moduleId}`;
            const cached = this.cache.get<Lesson[]>(cacheKey);
            if (cached) return cached;

            const lessonsRef = collection(db, LESSONS_COLLECTION);
            const q = query(
                lessonsRef,
                where('courseId', '==', courseId),
                where('moduleId', '==', moduleId)
            );
            const querySnapshot = await getDocs(q);

            const lessons = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lesson));

            lessons.sort((a, b) => (a.order || 0) - (b.order || 0));
            this.cache.set(cacheKey, lessons, CACHE_TTL);
            return lessons;
        } catch (error) {
            this.logError(error, { method: 'getLessons', courseId, moduleId });
            throw error;
        }
    }

    /**
     * Get lesson by ID
     */
    async getLessonById(lessonId: string): Promise<Lesson> {
        try {
            this.validate.validateLessonId(lessonId);

            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && (window as any).MOCK_LESSONS) {
                const mockLesson = ((window as any).MOCK_LESSONS as Lesson[]).find(l => l.id === lessonId);
                if (mockLesson) {
                    this.log('Returning MOCKED Lesson by ID for E2E testing');
                    return mockLesson;
                }
            }

            const cacheKey = `lesson_${lessonId}`;
            const cached = this.cache.get<Lesson>(cacheKey);
            if (cached) return cached;

            const mergedLesson = await this.getDoc(LESSONS_COLLECTION, lessonId) as Lesson;

            // LIVE PREVIEW BYPASS: If in dev mode, use local MD file content if available
            if (import.meta.env.DEV) {
                const localContent = await getLocalLessonContent(lessonId);
                if (localContent) {
                    this.log(`[LivePreview] Injecting local content for ${lessonId}`);
                    mergedLesson.content = localContent;
                }
            }

            this.cache.set(cacheKey, mergedLesson, CACHE_TTL);
            return mergedLesson;
        } catch (error: any) {
            if (error.code === 'NOT_FOUND') {
                throw new LessonError('Lesson not found');
            }
            this.logError(error, { method: 'getLessonById', lessonId });
            throw error;
        }
    }

    /**
     * Get lesson content
     */
    async getLessonContent(lessonId: string): Promise<Partial<Lesson>> {
        try {
            this.validate.validateLessonId(lessonId);

            const cacheKey = `lesson_content_${lessonId}`;
            const cached = this.cache.get<Partial<Lesson>>(cacheKey);
            if (cached) return cached;

            const lesson = await this.getLessonById(lessonId);

            const content: Partial<Lesson> = {
                id: lesson.id,
                title: lesson.title,
                description: lesson.description,
                type: lesson.type,
                videoUrl: lesson.videoUrl || null,
                videoDuration: lesson.videoDuration || null,
                textContent: lesson.textContent || null,
                content: lesson.content || undefined,
                minimalMinutes: lesson.minimalMinutes || 0,
                includeInTimeTracking: lesson.includeInTimeTracking || false
            };

            this.cache.set(cacheKey, content, CACHE_TTL);
            return content;
        } catch (error) {
            this.logError(error, { method: 'getLessonContent', lessonId });
            throw error;
        }
    }

    /**
     * Get all lessons for a course
     */
    async getAllCourseLessons(courseId: string): Promise<Lesson[]> {
        try {
            this.validate.validateCourseId(courseId);

            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && (window as any).MOCK_LESSONS) {
                this.log('Returning MOCKED All Course Lessons for E2E testing');
                return ((window as any).MOCK_LESSONS as Lesson[])
                    .filter(l => l.courseId === courseId)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
            }

            const cacheKey = `all_lessons_${courseId}`;
            const cached = this.cache.get<Lesson[]>(cacheKey);
            if (cached) return cached;

            const lessonsRef = collection(db, LESSONS_COLLECTION);
            const q = query(
                lessonsRef,
                where('courseId', '==', courseId)
            );
            const querySnapshot = await getDocs(q);

            const lessons = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lesson));

            lessons.sort((a, b) => (a.order || 0) - (b.order || 0));
            this.cache.set(cacheKey, lessons, CACHE_TTL);
            return lessons;
        } catch (error) {
            this.logError(error, { method: 'getAllCourseLessons', courseId });
            throw error;
        }
    }

    /**
     * Create new lesson
     */
    async createLesson(lessonData: Partial<Lesson>): Promise<Lesson> {
        try {
            if (typeof lessonData !== 'object' || !lessonData) {
                throw new ValidationError('Lesson data must be a valid object');
            }

            const lessonsRef = collection(db, LESSONS_COLLECTION);
            const newLesson = {
                ...lessonData,
                ...getTimestamps()
            };

            const docRef = await addDoc(lessonsRef, newLesson);
            this.cache.invalidate(`all_lessons_${lessonData.courseId}`);
            if (lessonData.moduleId) {
                this.cache.invalidate(`lessons_${lessonData.courseId}_${lessonData.moduleId}`);
            }

            return {
                id: docRef.id,
                ...newLesson
            } as Lesson;
        } catch (error) {
            this.logError(error, { method: 'createLesson' });
            throw error;
        }
    }

    /**
     * Update lesson
     */
    async updateLesson(lessonId: string, updates: Partial<Lesson>): Promise<Lesson> {
        try {
            this.validate.validateLessonId(lessonId);
            if (typeof updates !== 'object' || !updates) {
                throw new ValidationError('Updates must be a valid object');
            }

            const updateData = {
                ...updates,
                ...getUpdatedTimestamp()
            };

            await this.updateDoc(LESSONS_COLLECTION, lessonId, updateData);

            // Invalidate caches
            this.cache.invalidate(`lesson_${lessonId}`);
            this.cache.invalidate(`lesson_content_${lessonId}`);

            // Get the lesson to find courseId and moduleId for cache invalidation
            const lesson = await this.getLessonById(lessonId);
            this.cache.invalidate(`all_lessons_${lesson.courseId}`);
            this.cache.invalidate(`lessons_${lesson.courseId}_${lesson.moduleId}`);

            return lesson;
        } catch (error) {
            this.logError(error, { method: 'updateLesson', lessonId });
            throw error;
        }
    }

    /**
     * Delete lesson
     */
    async deleteLesson(lessonId: string): Promise<void> {
        try {
            this.validate.validateLessonId(lessonId);

            // Get the lesson first to find courseId and moduleId for cache invalidation
            const lesson = await this.getLessonById(lessonId);

            await this.deleteDoc(LESSONS_COLLECTION, lessonId);

            this.cache.invalidate(`lesson_${lessonId}`);
            this.cache.invalidate(`lesson_content_${lessonId}`);
            this.cache.invalidate(`all_lessons_${lesson.courseId}`);
            this.cache.invalidate(`lessons_${lesson.courseId}_${lesson.moduleId}`);
        } catch (error) {
            this.logError(error, { method: 'deleteLesson', lessonId });
            throw error;
        }
    }

    /**
     * Mark lesson as complete for user
     */
    async markComplete(userId: string, lessonId: string, courseId: string): Promise<void> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateLessonId(lessonId);
            this.validate.validateCourseId(courseId);

            const progressData = {
                userId,
                lessonId,
                courseId,
                completed: true,
                ...getCreatedTimestamp()
            };

            await this.setDoc(`users/${userId}/userProgress`, lessonId, progressData);
        } catch (error) {
            this.logError(error, { method: 'markComplete', userId, lessonId, courseId });
            throw error;
        }
    }

    /**
     * Reorder lessons
     */
    async reorderLessons(moduleId: string, lessonOrders: { lessonId: string; order: number }[]): Promise<void> {
        try {
            this.validate.validateModuleId(moduleId);
            if (!Array.isArray(lessonOrders)) {
                throw new ValidationError('Lesson orders must be an array');
            }

            const batch = this.batch();

            lessonOrders.forEach(({ lessonId, order }) => {
                const lessonRef = doc(db, LESSONS_COLLECTION, lessonId);
                batch.update(lessonRef, {
                    order,
                    ...getUpdatedTimestamp()
                });
                this.cache.invalidate(`lesson_${lessonId}`);
            });

            await batch.commit();
        } catch (error) {
            this.logError(error, { method: 'reorderLessons', moduleId });
            throw error;
        }
    }

    /**
     * Get next lesson
     */
    async getNextLesson(currentLessonId: string): Promise<Lesson | null> {
        try {
            this.validate.validateLessonId(currentLessonId);

            const currentLesson = await this.getLessonById(currentLessonId);
            const lessonsRef = collection(db, LESSONS_COLLECTION);
            const q = query(
                lessonsRef,
                where('moduleId', '==', currentLesson.moduleId)
            );

            const querySnapshot = await getDocs(q);

            const lessons = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as Lesson))
                .filter(l => (l.order || 0) > (currentLesson.order || 0))
                .sort((a, b) => (a.order || 0) - (b.order || 0));

            return lessons.length > 0 ? lessons[0] : null;
        } catch (error) {
            this.logError(error, { method: 'getNextLesson', currentLessonId });
            throw error;
        }
    }

    /**
     * Get previous lesson
     */
    async getPreviousLesson(currentLessonId: string): Promise<Lesson | null> {
        try {
            this.validate.validateLessonId(currentLessonId);

            const currentLesson = await this.getLessonById(currentLessonId);
            const lessonsRef = collection(db, LESSONS_COLLECTION);
            const q = query(
                lessonsRef,
                where('moduleId', '==', currentLesson.moduleId)
            );

            const querySnapshot = await getDocs(q);

            const lessons = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as Lesson))
                .filter(l => (l.order || 0) < (currentLesson.order || 0))
                .sort((a, b) => (b.order || 0) - (a.order || 0));

            return lessons.length > 0 ? lessons[0] : null;
        } catch (error) {
            this.logError(error, { method: 'getPreviousLesson', currentLessonId });
            throw error;
        }
    }
}

const lessonServices = new LessonService();

// Maintain backward compatibility for named exports
export const getLessons = (cId: string, mId: string) => lessonServices.getLessons(cId, mId);
export const getLessonById = (id: string) => lessonServices.getLessonById(id);
export const getLessonContent = (id: string) => lessonServices.getLessonContent(id);
export const getAllCourseLessons = (id: string) => lessonServices.getAllCourseLessons(id);
export const createLesson = (data: Partial<Lesson>) => lessonServices.createLesson(data);
export const updateLesson = (id: string, updates: Partial<Lesson>) => lessonServices.updateLesson(id, updates);
export const deleteLesson = (id: string) => lessonServices.deleteLesson(id);
export const markComplete = (uId: string, lId: string, cId: string) => lessonServices.markComplete(uId, lId, cId);
export const reorderLessons = (mId: string, orders: { lessonId: string; order: number }[]) => lessonServices.reorderLessons(mId, orders);
export const getNextLesson = (id: string) => lessonServices.getNextLesson(id);
export const getPreviousLesson = (id: string) => lessonServices.getPreviousLesson(id);

export default lessonServices;
